import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { BUSINESS } from "@/lib/constants";
import { SERVICE_PAGES } from "@/lib/servicePages";
import { Menu, Phone, X, ChevronDown } from "lucide-react";

const SERVICE_GROUPS = [
  {
    label: "Property Care",
    items: [
      "lawn-care",
      "lawn-mowing",
      "hedge-trimming",
      "tree-trimming",
      "patios",
      "backyard-makeover",
      "bin-washing",
      "junk-removal",
    ],
  },
  {
    label: "Winter Services",
    items: ["snow-removal", "snow-plow", "salting"],
  },
];

export default function Header({ onQuoteClick }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close dropdown on route change
  useEffect(() => {
    setServicesOpen(false);
    setMobileServicesOpen(false);
    setOpen(false);
  }, [pathname]);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setServicesOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const linkStyle = "text-sm text-white/70 hover:text-white transition-colors";

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-black/70 backdrop-blur-xl border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 lg:px-10 h-16 lg:h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="header-logo"
          className="flex items-center gap-3"
        >
          <img
            src={BUSINESS.logo}
            alt="Premium Property Care"
            loading="eager"
            decoding="async"
            width="44"
            height="44"
            className="h-9 w-9 lg:h-11 lg:w-11 object-contain rounded-md bg-white p-1"
          />
          <div className="leading-tight hidden sm:block">
            <div className="font-display text-[15px] font-bold tracking-tight">
              Premium <span className="text-[#8AB273]">Property</span> Care
            </div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">
              Niagara Region
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          <Link to="/" data-testid="nav-home" className={linkStyle}>
            Home
          </Link>

          <div
            className="relative"
            ref={dropdownRef}
            onMouseEnter={() => setServicesOpen(true)}
            onMouseLeave={() => setServicesOpen(false)}
          >
            <button
              data-testid="nav-services-trigger"
              onClick={() => setServicesOpen((v) => !v)}
              className={`${linkStyle} inline-flex items-center gap-1`}
              aria-expanded={servicesOpen}
            >
              Services
              <ChevronDown
                size={14}
                className={`transition-transform ${servicesOpen ? "rotate-180" : ""}`}
              />
            </button>
            {servicesOpen && (
              <div
                data-testid="nav-services-dropdown"
                className="absolute left-0 top-full mt-3 w-[560px] bg-[#0d1614]/95 backdrop-blur-xl border border-white/8 rounded-2xl p-5 shadow-2xl shadow-black/50"
              >
                <div className="grid grid-cols-2 gap-5">
                  {SERVICE_GROUPS.map((g) => (
                    <div key={g.label}>
                      <div className="label-eyebrow mb-3">{g.label}</div>
                      <ul className="space-y-1">
                        {g.items.map((slug) => {
                          const s = SERVICE_PAGES[slug];
                          if (!s) return null;
                          return (
                            <li key={slug}>
                              <Link
                                to={`/services/${slug}`}
                                data-testid={`nav-service-${slug}`}
                                className="block px-3 py-2 rounded-lg text-sm text-white/80 hover:bg-white/5 hover:text-white transition-colors"
                              >
                                {s.title}
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <Link
            to="/cleaning"
            data-testid="nav-cleaning"
            className={`${linkStyle} ${pathname === "/cleaning" ? "text-white" : ""}`}
          >
            Cleaning
          </Link>
          <a href="/#gallery" data-testid="nav-gallery" className={linkStyle}>
            Gallery
          </a>
          <a href="/#pricing" data-testid="nav-pricing" className={linkStyle}>
            Pricing
          </a>
        </nav>

        <div className="flex items-center gap-2">
          <a
            href={BUSINESS.phoneHref}
            data-testid="header-call-link"
            className="hidden md:inline-flex items-center gap-2 text-sm text-white/80 hover:text-white px-3 py-2"
          >
            <Phone size={15} className="text-[#8AB273]" />
            {BUSINESS.phone}
          </a>
          <button
            data-testid="header-quote-btn"
            onClick={onQuoteClick}
            className="hidden md:inline-flex items-center gap-2 bg-[#8AB273] text-black font-semibold text-sm px-4 py-2.5 rounded-full hover:bg-[#9DC585] transition-colors"
          >
            Request Free Quote
          </button>
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 text-white"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-black/95 border-t border-white/5 px-5 py-6 max-h-[calc(100vh-4rem)] overflow-y-auto"
        >
          <div className="flex flex-col gap-4">
            <Link
              to="/"
              onClick={() => setOpen(false)}
              className="text-white/80 text-base"
            >
              Home
            </Link>

            <div className="border-y border-white/10 py-2">
              <button
                data-testid="mobile-services-toggle"
                onClick={() => setMobileServicesOpen((v) => !v)}
                className="w-full flex items-center justify-between py-2 text-white/80 text-base"
                aria-expanded={mobileServicesOpen}
              >
                Services
                <ChevronDown
                  size={16}
                  className={`transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`}
                />
              </button>
              {mobileServicesOpen && (
                <div className="pt-3 pb-2 pl-2 space-y-4">
                  {SERVICE_GROUPS.map((g) => (
                    <div key={g.label}>
                      <div className="label-eyebrow mb-2 text-white/40">
                        {g.label}
                      </div>
                      <ul className="space-y-1">
                        {g.items.map((slug) => {
                          const s = SERVICE_PAGES[slug];
                          if (!s) return null;
                          return (
                            <li key={slug}>
                              <Link
                                to={`/services/${slug}`}
                                onClick={() => setOpen(false)}
                                data-testid={`mobile-nav-service-${slug}`}
                                className="block py-2 text-sm text-white/80"
                              >
                                {s.title}
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/cleaning"
              onClick={() => setOpen(false)}
              className="text-[#ede4cf] font-semibold text-base"
            >
              Cleaning Service →
            </Link>
            <a
              href="/#gallery"
              onClick={() => setOpen(false)}
              className="text-white/80 text-base"
            >
              Gallery
            </a>
            <a
              href="/#pricing"
              onClick={() => setOpen(false)}
              className="text-white/80 text-base"
            >
              Pricing
            </a>
            <a
              href={BUSINESS.phoneHref}
              className="flex items-center gap-2 text-white"
            >
              <Phone size={16} className="text-[#8AB273]" /> {BUSINESS.phone}
            </a>
            <button
              onClick={() => {
                setOpen(false);
                onQuoteClick();
              }}
              className="bg-[#8AB273] text-black font-semibold py-3 rounded-full"
            >
              Request Free Quote
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
